import React, { useMemo } from 'react'
import { LiveViewItemModel } from 'src/Libs/live-view'
import { LiveDataModel, PowermeterHardwareModel } from 'src/State/Storage/StorageModel'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { useBaseLiveValueItems } from './use-base-live-value-items'
import { IndicatorColor } from 'src/Elements/LiveView/types'

interface LiveViewItemsProps {
  batteryHomePower: number
  homeBatteryPower: number
  pvHomePower: number
  networkHomePower: number
  homeNetworkPower: number
  consumption: number
  homeConsumerPower: number
  consumers?: PowermeterHardwareModel[]
  producers?: PowermeterHardwareModel[]
  wallboxes?: WallboxHardwareModel[]
  liveData?: LiveDataModel
  offlineClientStorageIds?: string[]
  offlineIcon?: React.ReactNode
  homeWallboxPower: number
  trackerHomePower: number
  shallowLiveView?: boolean
  isBlockingTimeActive?: boolean
  isDynamicPowerPriceEnabled?: boolean
  showDynamicPrices?: boolean
  storageId?: string
}

/**
 * Hook that returns the live view items for the live view
 */
export const useLiveValueItems = ({
  batteryHomePower,
  homeBatteryPower,
  pvHomePower,
  networkHomePower,
  homeNetworkPower,
  consumption,
  homeConsumerPower,
  homeWallboxPower,
  consumers,
  liveData,
  offlineClientStorageIds,
  offlineIcon,
  producers,
  trackerHomePower,
  shallowLiveView,
  wallboxes,
  isBlockingTimeActive,
  isDynamicPowerPriceEnabled,
  showDynamicPrices,
  storageId,
}: LiveViewItemsProps): LiveViewItemModel[] => {
  const {
    batteryLiveItem,
    pvHomePowerLiveItem,
    evsesSubLiveItem,
    homeConsumptionLiveItem,
    externalConsumerSubLiveItem,
    consumptionPowerLiveItem,
    externalProduction,
    networkHomePowerLiveItem,
  } = useBaseLiveValueItems({
    batteryHomePower,
    homeBatteryPower,
    pvHomePower,
    consumption,
    homeConsumerPower,
    homeWallboxPower,
    consumers,
    liveData,
    offlineClientStorageIds,
    offlineIcon,
    producers,
    trackerHomePower,
    wallboxes,
    networkHomePower,
    homeNetworkPower,
    isBlockingTimeActive,
    isDynamicPowerPriceEnabled,
    showDynamicPrices,
    storageId,
  })

  const consumerCount = consumers?.length ?? 0
  const producerCount = producers?.length ?? 0
  const wallboxCount = wallboxes?.length ?? 0

  const liveBatteryLiveItem: LiveViewItemModel = useMemo(() => {
    return {
      ...batteryLiveItem,
      inColor: IndicatorColor.Green,
      outColor: IndicatorColor.Green,
    }
  }, [batteryLiveItem])

  const livePvHomePowerLiveItem: LiveViewItemModel = useMemo(() => {
    return {
      ...pvHomePowerLiveItem,
      inColor: IndicatorColor.Yellow,
      outColor: IndicatorColor.Green,
    }
  }, [pvHomePowerLiveItem])

  const liveNetworkHomePowerLiveItem: LiveViewItemModel = useMemo(() => {
    return {
      ...networkHomePowerLiveItem,
      inColor: IndicatorColor.Green,
      outColor: IndicatorColor.Yellow,
    }
  }, [networkHomePowerLiveItem])

  const liveWallboxItem: LiveViewItemModel | undefined = useMemo(() => {
    return wallboxCount && evsesSubLiveItem
      ? {
          ...evsesSubLiveItem,
          inColor: IndicatorColor.Green,
          outColor: IndicatorColor.Yellow,
        }
      : undefined
  }, [wallboxCount, evsesSubLiveItem])

  const liveHomeConsumptionLiveItem: LiveViewItemModel = useMemo(() => {
    return {
      ...homeConsumptionLiveItem,
      inColor: IndicatorColor.Green,
      outColor: IndicatorColor.Yellow,
    }
  }, [homeConsumptionLiveItem])

  const liveExternalConsumerSubLiveItem: LiveViewItemModel | undefined = useMemo(
    () =>
      consumerCount && externalConsumerSubLiveItem
        ? {
            ...externalConsumerSubLiveItem,
            inColor: IndicatorColor.Green,
            outColor: IndicatorColor.Yellow,
          }
        : undefined,
    [consumerCount, externalConsumerSubLiveItem]
  )

  const homeConsumptionPowerLiveItemChildren: LiveViewItemModel[] = []

  if (liveExternalConsumerSubLiveItem) homeConsumptionPowerLiveItemChildren.push(liveExternalConsumerSubLiveItem)
  if (liveWallboxItem) homeConsumptionPowerLiveItemChildren.push(liveWallboxItem)
  if (homeConsumptionPowerLiveItemChildren.length > 0) homeConsumptionPowerLiveItemChildren.push(liveHomeConsumptionLiveItem)

  const liveConsumptionPowerLiveItem: LiveViewItemModel = {
    ...consumptionPowerLiveItem,
    inColor: IndicatorColor.Green,
    outColor: IndicatorColor.Yellow,
    children: homeConsumptionPowerLiveItemChildren,
  }

  const liveExternalProduction: LiveViewItemModel = useMemo(() => {
    return {
      ...externalProduction,
      inColor: IndicatorColor.Green,
      outColor: IndicatorColor.Yellow,
    }
  }, [externalProduction])

  const items: LiveViewItemModel[] = []

  const addLiveViewItems = (): void => {
    if (shallowLiveView) {
      if (producerCount) items.push(liveExternalProduction)
      items.push(livePvHomePowerLiveItem)
      items.push(homeConsumptionPowerLiveItemChildren.length > 0 ? liveConsumptionPowerLiveItem : liveHomeConsumptionLiveItem)
      items.push(liveNetworkHomePowerLiveItem)
      items.push(liveBatteryLiveItem)
    } else {
      if (producerCount) items.push(liveExternalProduction)
      items.push(livePvHomePowerLiveItem, liveBatteryLiveItem)
      items.push(homeConsumptionPowerLiveItemChildren.length > 0 ? liveConsumptionPowerLiveItem : liveHomeConsumptionLiveItem)
      items.push(liveNetworkHomePowerLiveItem)
    }
  }

  addLiveViewItems()
  return items
}
